import { Grid } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import FormHelper from '../../../helpers/FormHelper';
import PartnerPmsService from '../../../services/PartnerPmsService';
import SelectField from '../../SelectField';
import { useUserContext } from '../../../contexts/UserContext';
import DateField from '../../DateField';
import TextField from '../../TextField';
import ValidationHelper from '../../../helpers/ValidationHelper';
import DefaultConstants from '../../../data/Constants';
import DateTimeHelper from '../../../helpers/DateTimeHelper';


interface IAddRoomBlockingDialogProps extends IDefaultDialogProps {
    room: IRoom;
    handleRefreshSelectedRoom: () => void;
}


const AddRoomBlockingDialog: React.FC<IAddRoomBlockingDialogProps> = ({
    open,
    handleClose,
    room,
    handleRefreshSelectedRoom
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();

    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [typeId, setTypeId] = useState<number | undefined>(undefined);
    const [description, setDescription] = useState<string>('');


    useEffect(() => {
        handleResetFields();
        // eslint-disable-next-line
    }, []);


    const handleResetFields = useCallback(() => {
        const now = DateTimeHelper.ToDate(undefined);

        setStartDate(now);
        setEndDate(now);
        setTypeId(undefined);
        setDescription('');
    }, []);
    

    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (room === undefined) {
            return;
        }

        let formData = FormHelper.Create(event);
        formData = FormHelper.Add(formData, 'roomId', room.id);

        PartnerPmsService.AddRoomBlocking(formData).then(() => {
            handleClose();
            handleRefreshSelectedRoom();
            handleResetFields();
        });

        // eslint-disable-next-line
    }, [room]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate('blockRoom')}
                onSubmit={handleSubmit}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                    <Grid item md={6} xs={12}>
                        <DateField
                            label={translate('startDate')}
                            format='DD/MM/YYYY'
                            name='startDate'
                            value={startDate}
                            onChange={value => setStartDate(value)}
                            disablePast={true}
                        />
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <DateField
                            label={translate('endDate')}
                            format='DD/MM/YYYY'
                            name='endDate'
                            value={endDate}
                            onChange={value => setEndDate(value)}
                            disablePast={true}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SelectField
                            label={translate('blockingType')}
                            name='typeId'
                            value={typeId}
                            onChange={e => setTypeId(e.target.value)}
                            data={(enterprise?.partnerPms.roomBlockingTypes ?? []).map(r => {
                                return {
                                    value: +r.id,
                                    text: r.description
                                };
                            })}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField 
                            label={translate('description')} 
                            name='description' 
                            value={description} 
                            onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setDescription)}
                            multiline={true}
                            minRows={6}
                            maxRows={6}
                        />
                    </Grid>
                </Grid>
            </Dialog>
        </>
    );
}

export default AddRoomBlockingDialog;